import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppContext } from '../AppContext';
import { collection, query, where, orderBy, limit, getDocs, startAfter, documentId } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';

import LoadingSpinner from './LoadingSpinner';
import FeedSkeleton from './FeedSkeleton';
import CommentModal from './CommentModal';
import ReelContainer from './ReelContainer';
import ReelPost from './ReelPost';
import ReelItem from './ReelItem'; // Import ReelItem
import useOnScreen from '../hooks/useOnScreen';
import VisionCreator from './visions/VisionCreator';
import MomentsFeedPage from './MomentsFeedPage'; // We'll use the structure and components from here
import { ReportModal } from './ReportModal';
import './Moments.css';

const MemoizedPost = React.memo(({ post, onVisible, ...props }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.5 });

    useEffect(() => {
        if (isVisible) {
            onVisible(props.index);
        }
    }, [isVisible, onVisible, props.index]);

    return (
        <div ref={ref} className="w-full h-full">
            <ReelPost post={post} {...props} />
        </div>
    );
});

const MemoizedReelItem = React.memo(({ moment, onVisible, ...props }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.5 });

    useEffect(() => {
        if (isVisible) {
            onVisible(props.index);
        }
    }, [isVisible, onVisible, props.index]);

    return (
        <div ref={ref} className="reel-item">
            <ReelItem moment={moment} {...props} />
        </div>
    );
});


const InnerCircleFeedPage = () => {
    const { db, appId, userConnections, userProfiles, userId, handleUserSelect, appFunctions, setMessage, innerCircleFeedType, showConfirmation } = useAppContext();
    const { t } = useTranslation();
    const [feedItems, setFeedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [commentModalPost, setCommentModalPost] = useState(null);
    const [visionCreatorOpen, setVisionCreatorOpen] = useState(false);
    const [sharedContent, setSharedContent] = useState(null);
    const [isMuted, setIsMuted] = useState(true);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportContent, setReportContent] = useState(null);

    const POSTS_PER_PAGE = 10;

    const innerCircleIds = useMemo(() => 
        userConnections
            .filter(conn => conn.tier === 'Inner Circle')
            .map(conn => conn.followingId)
    , [userConnections]);

    const profilesMap = useMemo(() => 
        new Map(userProfiles.map(p => [p.id, p]))
    , [userProfiles]);

    const fetchFeed = useCallback(async (loadMore = false) => {
        if (!hasMore && loadMore) return;
        setIsLoading(true);

        const safeInnerCircleIds = innerCircleIds.slice(0, 30);

        if (safeInnerCircleIds.length === 0) {
            setIsLoading(false);
            setHasMore(false);
            setFeedItems([]);
            return;
        }

        try {
            const collectionName = innerCircleFeedType === 'whispers' ? 'whispers' : 'moments';
            const contentRef = collection(db, `artifacts/${appId}/public/data/${collectionName}`);
            
            let q = query(
                contentRef,
                where('authorId', 'in', safeInnerCircleIds),
                orderBy('timestamp', 'desc'),
                limit(POSTS_PER_PAGE)
            );

            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const documentSnapshots = await getDocs(q);
            const newItems = documentSnapshots.docs.map(doc => {
                const data = doc.data();
                return { 
                    ...data, 
                    id: doc.id, 
                    contentType: innerCircleFeedType, // Add contentType for ReelPost
                    authorInfo: profilesMap.get(data.authorId) 
                };
            });

            setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            setFeedItems(prev => loadMore ? [...prev, ...newItems] : newItems);
            setHasMore(newItems.length === POSTS_PER_PAGE);

        } catch (error) {
            console.error(`Error fetching Inner Circle ${innerCircleFeedType}:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [innerCircleFeedType, innerCircleIds, db, appId, profilesMap, hasMore, lastVisible]);

    useEffect(() => {
        setFeedItems([]);
        setLastVisible(null);
        setHasMore(true);
        fetchFeed(false);
    }, [innerCircleFeedType]); // Refetch when feedType changes

    const handleVisibilityChange = useCallback((index) => {
        setCurrentIndex(index);
    }, []);
    
    // Reuse handlers from AnonymousFeed
    const handleLikeToggle = useCallback(async (entryId, authorId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        
        setFeedItems(prev => prev.map(item => {
            if (item.id === entryId) {
                const likes = item.likes || [];
                const hasLiked = likes.includes(userId);
                return { 
                    ...item, 
                    likes: hasLiked ? likes.filter(id => id !== userId) : [...likes, userId],
                    likesCount: (item.likesCount || 0) + (hasLiked ? -1 : 1)
                };
            }
            return item;
        }));

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId, authorId, reaction: 'like', contentType: innerCircleFeedType });
        } catch (error) { console.error("Error syncing 'like' reaction:", error); setMessage(t('anonymousFeed.likeError')); }
    }, [userId, appFunctions, setMessage, t, innerCircleFeedType]);

    const handleDislikeToggle = useCallback(async (entryId, authorId) => {
        if (!userId) { setMessage(t('anonymousFeed.signInRequired')); return; }
        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId, authorId, reaction: 'dislike', contentType: innerCircleFeedType });
        } catch (error) { console.error("Error syncing 'dislike' reaction:", error); setMessage(t('anonymousFeed.dislikeError')); }
    }, [userId, appFunctions, setMessage, t, innerCircleFeedType]);

    const handleAmplifyMoment = useCallback((momentId) => {
        showConfirmation({
            message: t('momentsFeed.amplifyConfirmation'),
            onConfirm: async () => {
                const amplifyMoment = httpsCallable(appFunctions, 'amplifyMoment');
                try {
                    await amplifyMoment({ momentId: momentId, amount: 10 });
                    setMessage(t('momentsFeed.amplifySuccess'));
                    setFeedItems(prev => prev.map(m => {
                        if (m.id === momentId) {
                            const newEchoesInvested = (m.echoesInvested || 0) + 10;
                            const newMoment = {
                                ...m,
                                echoesInvested: newEchoesInvested,
                            };
                            if (newEchoesInvested >= 30) {
                                newMoment.revealedTo = [...(m.revealedTo || []), userId];
                            }
                            return newMoment;
                        }
                        return m;
                    }));
                } catch (error) { setMessage(t('momentsFeed.amplifyFailed', { message: error.message })); }
            }
        });
    }, [appFunctions, setMessage, userId, t]);

    const handleShowComments = useCallback((post) => {
        setCommentModalPost(post);
    }, []);

    const handleShareToVision = useCallback((post) => {
        setSharedContent(post);
        setVisionCreatorOpen(true);
    }, []);

    const handleReport = useCallback((post) => {
        setReportContent({
            contentId: post.id,
            contentType: post.contentType || (innerCircleFeedType === 'whispers' ? 'whisper' : 'moment'),
            authorId: post.authorId
        });
        setShowReportModal(true);
    }, [innerCircleFeedType]);

    const getUserDisplayName = useCallback((authorId) => profilesMap.get(authorId)?.displayName || t('anonymousFeed.anonymousUser'), [profilesMap, t]);


    const renderContent = () => {
        if (isLoading && feedItems.length === 0) {
            return <FeedSkeleton />;
        }

        if (feedItems.length === 0) {
            return (
                <div className="text-center text-gray-300 p-8 bg-gray-800/50 rounded-lg">
                    <LucideIcons.Wind size={48} className="mx-auto mb-4 text-blue-400" />
                    <h3 className="text-xl font-bold">{t('innerCircleFeed.empty.title')}</h3>
                    <p className="mt-2">{t('innerCircleFeed.empty.description')}</p>
                </div>
            );
        }

        if (innerCircleFeedType === 'moments') {
            return (
                <ReelContainer>
                    {feedItems.map((item, index) => (
                        <div key={item.id} style={{ height: '100%', scrollSnapAlign: 'start' }}>
                            <MemoizedReelItem
                                moment={item}
                                index={index}
                                isActive={index === currentIndex}
                                onLike={handleLikeToggle}
                                onAmplify={handleAmplifyMoment}
                                onComment={handleShowComments}
                                onUserSelect={handleUserSelect}
                                isMuted={isMuted}
                                onMuteToggle={() => setIsMuted(prev => !prev)}
                                onShareToVision={handleShareToVision}
                                onVisible={handleVisibilityChange}
                                onReport={handleReport}
                            />
                        </div>
                    ))}
                </ReelContainer>
            );
        }

        return (
            <ReelContainer>
                {feedItems.map((item, index) => (
                    <div key={item.id} style={{ height: '100%', scrollSnapAlign: 'start' }}>
                        <MemoizedPost
                            post={item}
                            context="feed"
                            index={index}
                            userId={userId}
                            getUserDisplayName={getUserDisplayName}
                            onUserSelect={handleUserSelect}
                            handleLikeToggle={handleLikeToggle}
                            handleDislikeToggle={handleDislikeToggle}
                            onShowComments={handleShowComments}
                            onShareToVision={handleShareToVision}
                            onVisible={handleVisibilityChange}
                            onReport={handleReport}
                            // Pass empty functions for handlers not applicable to moments if needed
                            handleAmplify={() => {}}
                            handleSuperAmplify={() => {}}
                            handleDelete={() => {}}
                            handleAiAnalysis={() => {}}
                        />
                    </div>
                ))}
            </ReelContainer>
        );
    };

    return (
        <div className="p-4 sm:p-6 bg-black bg-opacity-50 rounded-lg shadow-lg max-w-3xl mx-auto text-white h-full flex flex-col">
            <div className="text-center mb-4 flex-shrink-0">
                <h2 className="text-3xl font-bold text-cyan-300 font-playfair mb-2">{t('nav.innerCircle')}</h2>
                <p className="text-sm text-gray-400">Exclusive content from your most trusted connections.</p>
            </div>

            <div className="flex-grow min-h-0 relative">
                {showReportModal && <ReportModal {...reportContent} onClose={() => setShowReportModal(false)} />}
                {commentModalPost && <CommentModal 
                    post={commentModalPost} 
                    onClose={() => setCommentModalPost(null)} 
                    contentType={commentModalPost.contentType}
                />}
                {visionCreatorOpen && <VisionCreator onClose={() => setVisionCreatorOpen(false)} sharedContent={sharedContent} />}
                {renderContent()}
            </div>
        </div>
    );
};

export default InnerCircleFeedPage;